import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Loading from '../../components/common/Loading/Loading';
import { SharedTokenService } from '../../services/SharedTokenService';
import { CHECKOUT_SUCCESS_DATA_QUERY_PARAM } from './checkout.constants';
import type {
  CheckoutPaymentResult,
  CheckoutPixPaymentInstructions,
  CheckoutSelectedPlan,
} from './checkout.types';
import CheckoutContainer from './components/CheckoutContainer';
import CheckoutHeader from './components/CheckoutHeader';
import CheckoutPixWaitingView from './components/CheckoutPixWaitingView';
import {
  CHECKOUT_SUCCESS_INVALID_URL_MESSAGE,
  buildAwaitingPaymentCheckoutResult,
  checkoutSuccessSnapshotToFormState,
  isCheckoutSuccessTokenExpired,
  parseCheckoutSuccessTokenPayload,
} from './checkoutSuccess.utils';

type CheckoutAwaitingPaymentPageState =
  | { status: 'loading' }
  | { status: 'invalid' }
  | { status: 'expired' }
  | {
    status: 'ready';
    form: ReturnType<typeof checkoutSuccessSnapshotToFormState>;
    selectedPlan: CheckoutSelectedPlan;
    checkoutResult: CheckoutPaymentResult;
    externalPaymentId: string;
    paymentInstructions: CheckoutPixPaymentInstructions;
    mode: 'checkout' | 'charge_checkout';
  };

const CheckoutAwaitingPaymentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<CheckoutAwaitingPaymentPageState>({ status: 'loading' });
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadAwaitingPaymentData = async () => {
      const token = searchParams.get(CHECKOUT_SUCCESS_DATA_QUERY_PARAM)?.trim();

      if (!token) {
        if (!cancelled) {
          setPageState({ status: 'invalid' });
        }
        return;
      }

      try {
        const decoded = await SharedTokenService.decodeCheckoutAwaitingPayment(token);
        const parsed = parseCheckoutSuccessTokenPayload(decoded.payload);

        if (!parsed) {
          if (!cancelled) {
            setPageState({ status: 'invalid' });
          }
          return;
        }

        if (isCheckoutSuccessTokenExpired(parsed.created_at)) {
          if (!cancelled) {
            setPageState({ status: 'expired' });
          }
          return;
        }

        const paymentInstructions = parsed.data.paymentInstructions?.method === 'pix'
          ? parsed.data.paymentInstructions
          : null;
        const externalPaymentId = (
          paymentInstructions?.externalPaymentId
          || parsed.data.externalPaymentId
          || ''
        ).trim();

        if (!paymentInstructions || !externalPaymentId) {
          if (!cancelled) {
            setPageState({ status: 'invalid' });
          }
          return;
        }

        if (!cancelled) {
          setPageState({
            status: 'ready',
            form: checkoutSuccessSnapshotToFormState(parsed.data),
            selectedPlan: parsed.data.selectedPlan,
            checkoutResult: buildAwaitingPaymentCheckoutResult(
              parsed.data,
              paymentInstructions,
              externalPaymentId,
            ),
            externalPaymentId,
            paymentInstructions,
            mode: parsed.data.mode === 'charge_checkout' ? 'charge_checkout' : 'checkout',
          });
        }
      } catch {
        if (!cancelled) {
          setPageState({ status: 'invalid' });
        }
      }
    };

    setPageState({ status: 'loading' });
    void loadAwaitingPaymentData();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (pageState.status === 'loading') {
    return (
      <CheckoutContainer>
        <CheckoutHeader title="" />
        <Loading />
      </CheckoutContainer>
    );
  }

  if (pageState.status === 'invalid' || pageState.status === 'expired') {
    return (
      <CheckoutContainer>
        <CheckoutHeader title="" />
        <EmptyState
          message={CHECKOUT_SUCCESS_INVALID_URL_MESSAGE}
          icon="link_off"
        />
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer>
      <CheckoutHeader title="" />
      <CheckoutPixWaitingView
        form={pageState.form}
        selectedPlan={pageState.selectedPlan}
        checkoutResult={pageState.checkoutResult}
        externalPaymentId={pageState.externalPaymentId}
        paymentInstructions={pageState.paymentInstructions}
        onRedirecting={() => setIsRedirecting(true)}
        isRedirecting={isRedirecting}
        mode={pageState.mode}
      />
    </CheckoutContainer>
  );
};

export default CheckoutAwaitingPaymentPage;
